import { FlowProductsSidebarItem } from '@flows/src/components/flow-products/FlowProductsSidebar';
import { AnvaQuote } from '@flows/src/types/anva';
import { SchemaYellowHiveQuoteCalculationJob } from 'wg-api-sdk';

export type FlowProductsNavigationMap = FlowProductsSidebarItem[];

/**
 * Creates a navigation map from initial job quotes (before jobs complete).
 * Extracts unique tag and index combinations to build sidebar navigation.
 */
export function createAnvaProductsNavigationMap(
  quotes: Pick<AnvaQuote, 'tag' | 'index' | 'risk_object_name'>[],
): FlowProductsNavigationMap {
  const tagMap = new Map<
    // Store anva tag id as string to for easier url matching
    string,
    {
      tag: AnvaQuote['tag'];
      indices: Map<number, { index: number; risk_object_name?: string }>;
    }
  >();

  // Extract unique tag/index combinations
  for (const quote of quotes) {
    const tagId = String(quote.tag.id);

    if (!tagMap.has(tagId)) {
      tagMap.set(tagId, {
        tag: quote.tag,
        indices: new Map(),
      });
    }

    const tagData = tagMap.get(tagId)!;
    if (!tagData.indices.has(quote.index)) {
      tagData.indices.set(quote.index, {
        index: quote.index,
        risk_object_name: quote.risk_object_name,
      });
    } else {
      // Update risk_object_name if it becomes available
      const existing = tagData.indices.get(quote.index)!;
      if (quote.risk_object_name && !existing.risk_object_name) {
        existing.risk_object_name = quote.risk_object_name;
      }
    }
  }

  // Convert to sidebar items format
  const sidebarItems: FlowProductsSidebarItem[] = Array.from(
    tagMap.values(),
  ).map(({ tag, indices }) => {
    const sortedIndices = Array.from(indices.values()).sort(
      (a, b) => a.index - b.index,
    );

    // If only one sub-item with index 0, don't create sub-items
    const subItems =
      sortedIndices.length === 1 && sortedIndices[0].index === 0
        ? undefined
        : sortedIndices.map(({ index, risk_object_name }) => ({
            key: `${tag.id}-${index}`,
            label: risk_object_name || `${tag.name} ${index + 1}`,
            index,
          }));

    return {
      key: String(tag.id),
      label: tag.name,
      iconName: tag.icon_key,
      subItems,
    };
  });

  return sidebarItems;
}

/**
 * Creates a navigation map from yellowhive quotes (before jobs complete).
 * Extracts unique insurance codes to build sidebar navigation.
 * Yellowhive doesn't have tags or indices, so this is simpler than Anva.
 */
export function createYellowhiveNavigationMap(
  quotes: SchemaYellowHiveQuoteCalculationJob[],
): FlowProductsNavigationMap {
  const insuranceMap = new Map<
    string,
    SchemaYellowHiveQuoteCalculationJob['insurance']
  >();

  // Extract unique insurance codes
  for (const quote of quotes) {
    const code = quote.insurance.code;
    if (!insuranceMap.has(code)) {
      insuranceMap.set(code, {
        code: quote.insurance.code,
        name: quote.insurance.name,
      });
    }
  }

  // Convert to sidebar items format
  const sidebarItems: FlowProductsSidebarItem[] = Array.from(
    insuranceMap.values(),
  ).map(({ code, name }) => ({
    key: code,
    label: name,
    // Yellowhive doesn't have icon keys, so no iconName
  }));

  return sidebarItems;
}
